import { onMounted, onUnmounted, watch } from 'vue';
import { useHttpClient } from './httpClient';
import mqtt from 'mqtt';
import type { VehicleType } from 'shared/vehicles/domain/vehicleType';
import type { ShapeResponse } from '@/shapes/api/models/shapeResponse';
import type { StorageAreaResponse } from '@/storageAreas/api/models/storageAreaResponse';
import { useSelectedSiteStore } from '@/sites/stores/selectedSiteStore';
import { storeToRefs } from 'pinia';
import type { ProductResponse } from '@/products/api/common/productResponse';
import { processShape } from '@/shapes/domain/processShape';
import type { VehicleResponse } from '@/vehicles/api/models/vehicleResponse';
import type { CoilResponse } from '@/coils/api/common/coilResponse';
import { useNewPosition } from '@/map/composables/newPosition';
import type { SettingResponse } from '@/settings/api/models/settingResponse';
import { useProductStore } from 'shared/products/stores/productStore';
import type { ProductGroupResponse } from '@/products/groups/api/productGroupResponse';
import { useProductGroupStore } from 'shared/products/groups/stores/productGroupStore';
import { useSettingStore } from 'shared/setting/stores/settingStore';
import { useShapeStore } from 'shared/shapes/stores/shapeStore';
import { useStorageAreaStore } from 'shared/storageAreas/stores/storageAreaStore';
import { useVehicleStore } from 'shared/vehicles/stores/vehicleStore';
import { useCoilStore } from 'shared/coils/stores/coilStore';
import { getBasePath } from './basePathApi';
import { useProductionOrderStore } from 'shared/productionOrders/stores/productionOrderStore';
import type { ProductionOrder } from 'shared/productionOrders/domain/productionOrder';
import type { ServerSession } from 'shared/serverSessions/domain/serverSession';
import { useServerSessionStore } from 'shared/serverSessions/stores/serverSessionStore';



export function useMqttClient() {
  const selectedSite = useSelectedSiteStore();
  const mqttAuth = useMqttAuth();
  const newPosition = useNewPosition();
  const storageAreaStore = useStorageAreaStore();
  const coilStore = useCoilStore();
  const productStore = useProductStore();
  const vehicleStore = useVehicleStore();
  const shapeStore = useShapeStore();
  const settingStore = useSettingStore();
  const productGroupStore = useProductGroupStore();
  const productionOrderStore = useProductionOrderStore();
  const serverSessionStore = useServerSessionStore();

  async function getMqttBasePath(): Promise<string> {
    const basePath = await getBasePath();
    return basePath + 'mqtt';
  }

  const { siteId: selectedSiteId } = storeToRefs(selectedSite);

  let client: mqtt.MqttClient | null = null;

  onMounted(async () => {
    const basePath = await getMqttBasePath();
    console.log('Base path for MQTT:', basePath);
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const url = `${protocol}//${window.location.host}${basePath}`;

    const auth = await mqttAuth.execute();
    client = await mqtt.connectAsync(url, {
      clientId: crypto.randomUUID(),
      username: auth.userName,
      password: auth.password,
    });

    client.on('message', (topic, payload) => {
      if (topic.endsWith('position')) {
        notifyPosition(payload);
      } else if (topic.endsWith('storagearea')) {
        notifyStorageArea(payload);
      } else if (topic.endsWith('shape')) {
        notifyShape(payload);
      } else if (topic.endsWith('product')) {
        notifyProduct(payload);
      } else if (topic.endsWith('vehicle')) {
        notifyVehicle(payload);
      } else if (topic.endsWith('coil')) {
        notifyCoil(payload);
      } else if (topic.endsWith('setting')) {
        notifySettings(payload);
      } else if (topic.endsWith('productGroup')) {
        notifyProductGroup(payload);
      } else if (topic.endsWith('productionorder')) {
        notifyProductionOrder(payload);
      } else if (topic.endsWith('server/session')) {
        notifyServerSession(payload);
      }
    });

    const siteId = selectedSiteId.value;

    if (siteId) {
      subscribe(siteId);
    }
  });

  onUnmounted(async () => {
    if (!client) return;
    await client.endAsync();
  });

  watch(selectedSiteId, (newSiteId, oldSiteId) => {
    if (oldSiteId !== undefined) {
      unsubscribe(oldSiteId);
    }

    if (newSiteId !== undefined) {
      subscribe(newSiteId);
    }
  });

  function subscribe(siteId: number) {
    if (!client) return;

    client.subscribe(`silog/${siteId}/position`);
    client.subscribe(`silog/${siteId}/storagearea`);
    client.subscribe(`silog/${siteId}/shape`);
    client.subscribe(`silog/${siteId}/product`);
    client.subscribe(`silog/${siteId}/vehicle`);
    client.subscribe(`silog/${siteId}/coil`);
    client.subscribe(`silog/${siteId}/setting`);
    client.subscribe(`silog/${siteId}/productGroup`);
    client.subscribe(`silog/${siteId}/productionorder`);
    client.subscribe('silog/server/session');
  }

  function unsubscribe(siteId: number) {
    if (!client) return;
    client.unsubscribe(`silog/${siteId}/position`);
    client.unsubscribe(`silog/${siteId}/storagearea`);
    client.unsubscribe(`silog/${siteId}/shape`);
    client.unsubscribe(`silog/${siteId}/product`);
    client.unsubscribe(`silog/${siteId}/vehicle`);
    client.unsubscribe(`silog/${siteId}/coil`);
    client.unsubscribe(`silog/${siteId}/setting`);
    client.unsubscribe(`silog/${siteId}/productGroup`);
    client.unsubscribe(`silog/${siteId}/productionorder`);
    client.unsubscribe('silog/server/session');
  }

  function notifyPosition(payload: Buffer) {
    const json = JSON.parse(payload.toString()) as PositionMessage;
    newPosition.emit(json);
  }

  function notifyStorageArea(payload: Buffer) {
    const json = JSON.parse(payload.toString()) as StorageAreaResponse;
    if (json.isRemoved) {
      storageAreaStore.remove(json.id);
    } else {
      storageAreaStore.add(json);
    }
  }

  function notifyShape(payload: Buffer) {
    const json = JSON.parse(payload.toString()) as ShapeResponse;
    processShape(json);
    if (json.isRemoved) {
      shapeStore.remove(json.id);
    } else {
      shapeStore.add(json);
    }
  }

  function notifyProduct(payload: Buffer) {
    const json = JSON.parse(payload.toString()) as ProductResponse;
    if (json.isRemoved) {
      productStore.remove(json.id);
    } else {
      productStore.add(json);
    }
  }

  function notifyVehicle(payload: Buffer) {
    const vehicle = JSON.parse(payload.toString()) as VehicleResponse;
    vehicleStore.add(vehicle);
  }

  function notifyCoil(payload: Buffer) {
    const coil = JSON.parse(payload.toString()) as CoilResponse;
    if (coil.isRemoved) {
      coilStore.remove(coil.id);
    } else {
      coilStore.add(coil);
    }
  }

  function notifySettings(payload: Buffer) {
    const setting = JSON.parse(payload.toString()) as SettingResponse;
    settingStore.setting = setting;
  }

  function notifyProductGroup(payload: Buffer) {
    const group = JSON.parse(payload.toString()) as ProductGroupResponse;
    if (group.isRemoved) {
      productGroupStore.remove(group.id);
    } else {
      productGroupStore.add(group);
    }
  }

  function notifyProductionOrder(payload: Buffer) {
    const productionOrder = JSON.parse(payload.toString()) as ProductionOrder;
    if (productionOrder.isRemoved) {
      productionOrderStore.remove(productionOrder.id);
    } else {
      productionOrderStore.add(productionOrder);
    }
  }

  function notifyServerSession(payload: Buffer) {
    const message = JSON.parse(payload.toString()) as ServerSession;
    serverSessionStore.merge(message);
  }
}

export type PositionListener = (position: PositionMessage) => void;

function useMqttAuth() {
  const httpClient = useHttpClient();

  return {
    execute,
  };

  async function execute() {
    const headers = httpClient.defaultHeaders();

    const request = new Request(httpClient.siteUrl('authmqtt'), {
      headers,
    });

    const response = await httpClient.execute(request);

    const json = (await response.json()) as MqttAuthInformation;
    return json;
  }
}

interface MqttAuthInformation {
  clientId: string;
  userName: string;
  password: string;
}

export interface PositionMessage {
  vehicleId: string;
  vehicleType: VehicleType;
  clientTimestampUTC: string;
  latitude: number;
  longitude: number;
  altitude: number;
  heading: number;
  speed: number;
  gpsQuality: string;
  numberOfSatellites: number;
  ageOfDifferentialData: number;
}
